import { useCallback, useEffect, useMemo, useState } from 'react'

import { isEmpty } from 'lodash-es'

import { useInProgressSearch } from './useInProgressSearch'
import { useSearchAgeAndType } from './useSearchAgeAndType'
import { Appointment, ProviderProgramTaxonomy } from '../../../models'
import { Patient } from '../../../models/healthPlan/HealthPlan'
import {
  GetAllTriageSearchesActions,
  GetNextQuestionActions,
  GetTriageMetadataActions,
  NewSearchData,
  PreselectedTriageData,
  SearcherInfo,
  TriageSearch,
  TriageSearchStatus,
} from '../../../models/searchForCare/SearchForCare'
import { TriageScreener, TriageScreenerNames } from '../../../models/triageScreener/TriageScreener'
import { User } from '../../../models/user/User'
import { TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC } from '../../centerOfExcellence/types'
import { isToolkitTypeCenterOfExcellence } from '../../centerOfExcellence/utils'
import { PRODUCT_TYPE } from '../../common/constants/productType'
import { TOOLKIT_TYPE } from '../../wellnessToolkit/types'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '../constants'
import {
  getClientSelectedSubTopicKey,
  getClientSelectedTopicKey,
  getNewSearchData,
  shouldSkipPrimaryNeeds,
} from '../utils'

export interface useCommonTriageSearchDataArgs {
  searches: TriageSearch[]
  user: User
  searcherInfo: SearcherInfo | null
  selectedSearch?: TriageSearch
  triageMetadata: TriageScreener[]
  triageQuestions: TriageScreenerNames[]
  loadingNextQuestion: boolean
  updatingSearch: boolean
  appointments: Appointment[]
  matchFor: SEARCHER_TYPE_TO_MATCH_FOR
  log: (searchId?: string) => void
  getAllTriageSearches: (arg: GetAllTriageSearchesActions) => Promise<any>
  getTriageMetadata: (arg: GetTriageMetadataActions) => Promise<any>
  postTriageSearch: (arg: NewSearchData) => Promise<any>
  setSelectedSearch: (searchId: string) => void
  setTriageQuestions: (arg: Array<TriageScreenerNames | undefined | string>) => void
  getNextTriageQuestion: (arg: GetNextQuestionActions) => Promise<any>
  logException: (message: string, extras: any, error: unknown) => void
  shouldCreateNewSearch?: boolean
  triageScreenersMetadataVersion?: string
  preselectedTriageData?: PreselectedTriageData
  programPreference?: ProviderProgramTaxonomy
  productType: PRODUCT_TYPE
  toolkitType?: TOOLKIT_TYPE
  toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
  patient?: Patient
  isSearchToolkitAware?: boolean
}

/**
 * Handles initial apis calls for triage searches
 */
export const useCommonTriageSearchData = ({
  searches,
  user,
  searcherInfo,
  selectedSearch,
  triageMetadata,
  triageQuestions,
  loadingNextQuestion,
  updatingSearch,
  appointments,
  matchFor,
  log,
  getAllTriageSearches,
  getTriageMetadata,
  postTriageSearch,
  setSelectedSearch,
  setTriageQuestions,
  getNextTriageQuestion,
  logException,
  shouldCreateNewSearch,
  triageScreenersMetadataVersion,
  preselectedTriageData,
  programPreference,
  productType,
  toolkitType,
  toolkitSubTopic,
  patient,
  isSearchToolkitAware,
}: useCommonTriageSearchDataArgs) => {
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false)
  const [fetchInitialCalled, setFetchInitialCalled] = useState(false)
  const [preselectedTriageDataHandled, setPreselectedTriageDataHandled] = useState(false)
  const { id: userId, gender, customers, dob, customerCountries } = user || {}
  const preselectedTriageDataExists =
    !isEmpty(preselectedTriageData?.primaryNeeds) || !isEmpty(preselectedTriageData?.whatElseOnYourMindText)

  const eligibilityType = useMemo(
    () => patient?.eligibilityType ?? user?.eligibilityType,
    [patient?.eligibilityType, user?.eligibilityType],
  )

  const searchDob = searcherInfo?.childLyraId ? searcherInfo?.dob : dob

  const newSearchData = useMemo(
    () =>
      getNewSearchData({
        dob: searchDob,
        userId,
        gender: gender?.gender || 'unknown',
        employer: customers?.name,
        type: searcherInfo?.type,
        childUserId: searcherInfo?.childLyraId,
        productType,
        eligibilityType: eligibilityType ?? undefined,
        countryName: customerCountries?.countryName,
      }),
    [
      customerCountries?.countryName,
      customers?.name,
      searchDob,
      eligibilityType,
      gender?.gender,
      productType,
      searcherInfo?.type,
      searcherInfo?.childLyraId,
      userId,
    ],
  )
  const inProgressSearch = useInProgressSearch(searches, appointments)
  const getMetadataAge = useSearchAgeAndType()
  const isSearchProgramAware = programPreference !== undefined
  const isCOEFlow = isToolkitTypeCenterOfExcellence(toolkitType, toolkitSubTopic)
  const searcherInfoExists = !!searcherInfo
  // Initial fetch to get all searches
  useEffect(() => {
    const fetchInitialSearchesAndMetadata = async (userId: string) => {
      const { age } = getMetadataAge({ dob: searchDob, matchFor, age: searcherInfo?.age })
      try {
        setFetchInitialCalled(true)
        await Promise.all([
          getAllTriageSearches({ userId, hideError: [404] }),
          getTriageMetadata({ age, version: triageScreenersMetadataVersion }),
        ])
      } catch (error) {
        logException('Unable to fetch initial searches metadata', { source: 'useTriageSearchData.ts' }, error)
      } finally {
        setHasLoadedInitial(true)
      }
    }
    if (
      !hasLoadedInitial &&
      !!userId &&
      !shouldCreateNewSearch &&
      !preselectedTriageDataExists &&
      searcherInfoExists &&
      !fetchInitialCalled &&
      !updatingSearch
    ) {
      fetchInitialSearchesAndMetadata(userId)
    }
  }, [
    searchDob,
    getAllTriageSearches,
    getMetadataAge,
    getTriageMetadata,
    hasLoadedInitial,
    matchFor,
    searcherInfo?.age,
    logException,
    userId,
    shouldCreateNewSearch,
    fetchInitialCalled,
    triageScreenersMetadataVersion,
    updatingSearch,
    preselectedTriageDataExists,
    preselectedTriageDataHandled,
    searcherInfoExists,
  ])

  const createNewSearch = useCallback(
    (newSearchData: NewSearchData) => {
      try {
        postTriageSearch(newSearchData)
      } catch (error) {
        logException('Unable to create new search', { source: 'useTriageSearchData.ts' }, error)
      }
    },
    [postTriageSearch, logException],
  )

  useEffect(() => {
    if (hasLoadedInitial && !isEmpty(newSearchData) && !selectedSearch && !updatingSearch) {
      if (isToolkitTypeCenterOfExcellence(toolkitType, toolkitSubTopic) || isSearchToolkitAware) {
        // Center Of Excellence and toolkit aware searches should not use programPreference
        createNewSearch({
          lyra_id: newSearchData.lyra_id,
          body: {
            ...newSearchData.body,
            clientSelectedTopic: getClientSelectedTopicKey(toolkitType),
            clientSelectedSubTopic: getClientSelectedSubTopicKey(toolkitSubTopic),
            productType,
          },
        })
      } else if (programPreference) {
        createNewSearch({
          lyra_id: newSearchData.lyra_id,
          body: {
            ...newSearchData.body,
            clientSelectedTopic: getClientSelectedTopicKey(toolkitType),
            programPreference,
            productType,
          },
        })
      } else if (!searches.length) {
        // If searches are still empty after initial load then create a new search
        createNewSearch(newSearchData)
      } else if (searches.length) {
        if (inProgressSearch?.id) {
          // If a search is already in progress set it to active
          setSelectedSearch(inProgressSearch.id)
          log(inProgressSearch?.id)
        } else {
          // If there are no active searches then create a new one
          createNewSearch(newSearchData)
        }
      }
    }
  }, [
    hasLoadedInitial,
    inProgressSearch?.id,
    log,
    newSearchData,
    postTriageSearch,
    searches.length,
    selectedSearch,
    logException,
    setSelectedSearch,
    updatingSearch,
    createNewSearch,
    programPreference,
    productType,
    toolkitType,
    toolkitSubTopic,
    isSearchToolkitAware,
  ])

  // Handles creating new search from provider results/recommendations screen
  useEffect(() => {
    if (shouldCreateNewSearch && !isEmpty(newSearchData) && !updatingSearch) {
      createNewSearch(newSearchData)
    }
  }, [createNewSearch, newSearchData, shouldCreateNewSearch, updatingSearch])

  // Handles creating new search for primary need that was preselected from homebase
  useEffect(() => {
    if (preselectedTriageDataExists && !preselectedTriageDataHandled && !isEmpty(newSearchData) && !updatingSearch) {
      createNewSearch(newSearchData)
      setPreselectedTriageDataHandled(true)
    }
  }, [
    createNewSearch,
    newSearchData,
    preselectedTriageDataHandled,
    preselectedTriageDataExists,
    shouldCreateNewSearch,
    updatingSearch,
  ])

  // Handle in progress searches
  useEffect(() => {
    if (
      !!selectedSearch &&
      selectedSearch?.triage_status === TriageSearchStatus?.IN_PROGRESS &&
      !triageQuestions.length &&
      triageMetadata.length
    ) {
      // If selected search already has triage_metadata we should add them to triageQuestions in the store
      if (selectedSearch.triage_metadata.length) {
        // TODO: We shouldn't have to map id to names
        const searchMetadataNames = selectedSearch?.triage_metadata.map(
          (id) => triageMetadata.find((metadata) => metadata.id === id)?.name,
        )
        !!searchMetadataNames.length && setTriageQuestions(searchMetadataNames)
      } else if (!loadingNextQuestion) {
        // If selected search does not have triage_metadata we should get the first question
        getNextTriageQuestion({
          id: selectedSearch.id,
          hideError: [404],
          skipScreener: shouldSkipPrimaryNeeds({
            isSearchProgramAware,
            isSearchToolkitAware,
            isCOEFlow,
          }),
        })
      }
    }
  }, [
    getNextTriageQuestion,
    loadingNextQuestion,
    selectedSearch,
    setTriageQuestions,
    triageMetadata,
    triageQuestions.length,
    isSearchProgramAware,
    isCOEFlow,
    toolkitType,
    isSearchToolkitAware,
  ])

  return { hasLoadedInitial: preselectedTriageDataExists ? preselectedTriageDataHandled : hasLoadedInitial }
}
